
import { TestResult } from "@/hooks/useTestResults";
import { Achievement } from "./types";
import { checkImprovement, checkConsistency, checkMastery, checkCategoryMastery } from "./checkers";
import { findImprovementDate, findConsistencyDate, findMasteryDate, findCategoryMasteryDate } from "./dateFinders";

export const generateAchievements = (testResults: TestResult[]): Achievement[] => {
  const sortedResults = [...testResults].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  const achievements: Achievement[] = [
    {
      id: "first-test",
      title: "First Test Complete",
      description: "Completed your first assessment",
      earned: testResults.length > 0,
      date: testResults[0]?.created_at,
      type: 'test'
    },
    {
      id: "perfect-score",
      title: "Perfect Score",
      description: "Achieved 100% on a test",
      earned: testResults.some(result => result.score === 100),
      date: sortedResults.find(result => result.score === 100)?.created_at,
      type: 'test'
    },
    {
      id: "improvement",
      title: "Steady Progress",
      description: "Improved score three times in a row",
      earned: checkImprovement(testResults),
      date: findImprovementDate(sortedResults),
      type: 'test'
    },
    {
      id: "consistency",
      title: "Consistent Performer",
      description: "Scored above 80% in three consecutive tests",
      earned: checkConsistency(testResults),
      date: findConsistencyDate(sortedResults),
      type: 'test'
    },
    {
      id: "mastery",
      title: "Subject Mastery",
      description: "Achieved 90% or higher in all tests of a category",
      earned: checkMastery(testResults),
      date: findMasteryDate(sortedResults),
      type: 'test'
    },
    {
      id: "motor-milestone",
      title: "Motor Skills Master",
      description: "Maintains a 90% or higher average in Motor Skills tests",
      earned: checkCategoryMastery(testResults, 'Motor Skills'),
      date: findCategoryMasteryDate(sortedResults, 'Motor Skills'),
      type: 'milestone'
    },
    {
      id: "language-milestone",
      title: "Language Explorer",
      description: "Maintains a 90% or higher average in Language tests",
      earned: checkCategoryMastery(testResults, 'Language'),
      date: findCategoryMasteryDate(sortedResults, 'Language'),
      type: 'milestone'
    },
    {
      id: "cognitive-milestone",
      title: "Cognitive Champion",
      description: "Maintains a 90% or higher average in Cognitive tests",
      earned: checkCategoryMastery(testResults, 'Cognitive'),
      date: findCategoryMasteryDate(sortedResults, 'Cognitive'),
      type: 'milestone'
    },
    {
      id: "independence-milestone",
      title: "Independence Pioneer",
      description: "Maintains a 90% or higher average in Independent Skills tests",
      earned: checkCategoryMastery(testResults, 'Independent Skills'),
      date: findCategoryMasteryDate(sortedResults, 'Independent Skills'),
      type: 'milestone'
    },
    {
      id: "social-emotional-milestone",
      title: "Social Star",
      description: "Maintains a 90% or higher average in Social and Emotional Skills tests",
      earned: checkCategoryMastery(testResults, 'Social and Emotional Skills'),
      date: findCategoryMasteryDate(sortedResults, 'Social and Emotional Skills'),
      type: 'milestone'
    }
  ];

  return achievements;
};
